import React from 'react';
import {
  Container,
  Typography,
  Box,
  Chip,
  Avatar,
  Divider,
  Button,
  Paper,
  Skeleton,
  Alert,
  Link as MuiLink,
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import GavelIcon from '@mui/icons-material/Gavel';
import { Helmet } from 'react-helmet-async';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { useBlogPost, useBlogIndex } from '../hooks/useBlogData';
import { trackEvent } from '../components/GoogleAnalytics';
import ShareButton from '../components/ShareButton';
import type { BlogPost as BlogPostType } from '../types/blog';

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

const BlogPostSEO: React.FC<{ post: BlogPostType }> = ({ post }) => {
  const url = `https://robertgarza.us/blog/${post.slug}`;
  const defaultImage = 'https://robertgarza.us/social-share.png';

  return (
    <Helmet>
      <title>{post.seoTitle}</title>
      <meta name="description" content={post.seoDescription} />
      <link rel="canonical" href={url} />
      <meta property="og:type" content="article" />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={post.seoTitle} />
      <meta property="og:description" content={post.seoDescription} />
      <meta property="og:image" content={post.featuredImage || defaultImage} />
      <meta property="article:published_time" content={post.publishedDate} />
      {post.updatedDate && <meta property="article:modified_time" content={post.updatedDate} />}
      <meta property="article:author" content={post.author.name} />
      <meta property="article:section" content={post.category} />
      {post.tags.map((tag) => (
        <meta key={tag} property="article:tag" content={tag} />
      ))}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={url} />
      <meta name="twitter:title" content={post.seoTitle} />
      <meta name="twitter:description" content={post.seoDescription} />
      <meta name="twitter:image" content={post.featuredImage || defaultImage} />
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: post.title,
          description: post.seoDescription,
          image: post.featuredImage || defaultImage,
          datePublished: post.publishedDate,
          dateModified: post.updatedDate || post.publishedDate,
          author: {
            '@type': 'Person',
            name: post.author.name,
            url: post.author.social?.website,
          },
          publisher: {
            '@type': 'Organization',
            name: 'RobertGarza.us',
            logo: {
              '@type': 'ImageObject',
              url: 'https://robertgarza.us/images/logo.png',
            },
          },
          mainEntityOfPage: {
            '@type': 'WebPage',
            '@id': url,
          },
          wordCount: post.wordCount,
          articleSection: post.category,
          keywords: post.tags.join(', '),
        })}
      </script>
    </Helmet>
  );
};

const AuthorBio: React.FC<{ author: BlogPostType['author'] }> = ({ author }) => (
  <Paper elevation={0} sx={{ p: 3, bgcolor: 'action.hover', borderRadius: 2, mt: 4 }}>
    <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
      <Avatar src={author.avatar || undefined} alt={author.name} sx={{ width: 64, height: 64, flexShrink: 0 }}>
        {author.name.charAt(0)}
      </Avatar>
      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {author.name}
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {author.title}
        </Typography>
        <Typography variant="body2" sx={{ mt: 1 }}>
          {author.bio}
        </Typography>
        {author.social && (
          <Box sx={{ mt: 1.5, display: 'flex', gap: 2 }}>
            {author.social.website && (
              <MuiLink href={author.social.website} target="_blank" rel="noopener" variant="body2">
                Website
              </MuiLink>
            )}
            {author.social.email && (
              <MuiLink href={`mailto:${author.social.email}`} variant="body2">
                Email
              </MuiLink>
            )}
            {author.social.twitter && (
              <MuiLink
                href={`https://twitter.com/${author.social.twitter}`}
                target="_blank"
                rel="noopener"
                variant="body2"
              >
                Twitter
              </MuiLink>
            )}
          </Box>
        )}
      </Box>
    </Box>
  </Paper>
);

const RelatedBills: React.FC<{ billSlugs: string[] }> = ({ billSlugs }) => {
  if (billSlugs.length === 0) return null;

  return (
    <Paper elevation={0} sx={{ p: 3, bgcolor: 'primary.main', color: 'primary.contrastText', borderRadius: 2, mt: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <GavelIcon />
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Related Reform Bills
        </Typography>
      </Box>
      <Typography variant="body2" sx={{ mb: 2, opacity: 0.9 }}>
        Learn more about the legislation mentioned in this article.
      </Typography>
      <Button
        component={RouterLink}
        to="/family-court-reform-bills"
        variant="outlined"
        onClick={() => trackEvent('cta_click', 'engagement', 'View All Reform Bills - Blog Post')}
        sx={{
          color: 'primary.contrastText',
          borderColor: 'primary.contrastText',
          '&:hover': {
            borderColor: 'primary.contrastText',
            bgcolor: 'rgba(255,255,255,0.1)',
          },
        }}
      >
        View All Reform Bills
      </Button>
    </Paper>
  );
};

const ArticleNavigation: React.FC<{ currentSlug: string }> = ({ currentSlug }) => {
  const { data: blogIndex } = useBlogIndex();

  if (!blogIndex || blogIndex.posts.length <= 1) return null;

  const currentIndex = blogIndex.posts.findIndex((p) => p.slug === currentSlug);
  if (currentIndex === -1) return null;

  const prevPost = currentIndex < blogIndex.posts.length - 1 ? blogIndex.posts[currentIndex + 1] : null;
  const nextPost = currentIndex > 0 ? blogIndex.posts[currentIndex - 1] : null;

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        gap: 2,
        mt: 4,
        pt: 4,
        borderTop: 1,
        borderColor: 'divider',
      }}
    >
      {prevPost ? (
        <Button
          component={RouterLink}
          to={`/blog/${prevPost.slug}`}
          startIcon={<ArrowBackIcon />}
          sx={{ textAlign: 'left', flexDirection: 'column', alignItems: 'flex-start' }}
        >
          <Typography variant="caption" color="text.secondary">
            Previous Article
          </Typography>
          <Typography
            variant="body2"
            sx={{
              maxWidth: 200,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {prevPost.title}
          </Typography>
        </Button>
      ) : (
        <Box />
      )}
      {nextPost ? (
        <Button
          component={RouterLink}
          to={`/blog/${nextPost.slug}`}
          endIcon={<ArrowForwardIcon />}
          sx={{ textAlign: 'right', flexDirection: 'column', alignItems: 'flex-end' }}
        >
          <Typography variant="caption" color="text.secondary">
            Next Article
          </Typography>
          <Typography
            variant="body2"
            sx={{
              maxWidth: 200,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {nextPost.title}
          </Typography>
        </Button>
      ) : (
        <Box />
      )}
    </Box>
  );
};

const ArticleSkeleton: React.FC = () => (
  <Container maxWidth="md" sx={{ py: 4 }}>
    <Skeleton variant="rectangular" width={100} height={32} sx={{ mb: 2, borderRadius: 1 }} />
    <Skeleton variant="text" sx={{ fontSize: '2.5rem' }} />
    <Skeleton variant="text" sx={{ fontSize: '2.5rem', width: '80%', mb: 2 }} />
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
      <Skeleton variant="circular" width={40} height={40} />
      <Skeleton variant="text" width={150} />
      <Skeleton variant="text" width={100} />
    </Box>
    <Divider sx={{ mb: 4 }} />
    {[1, 2, 3, 4, 5].map((i) => (
      <Skeleton key={i} variant="text" sx={{ mb: 1 }} />
    ))}
    <Skeleton variant="text" sx={{ width: '60%', mb: 3 }} />
    {[1, 2, 3].map((i) => (
      <Skeleton key={`p2-${i}`} variant="text" sx={{ mb: 1 }} />
    ))}
  </Container>
);

const BlogPostPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { data: post, isLoading, error } = useBlogPost(slug);

  if (!isLoading && !post && !error) {
    return (
      <Container maxWidth="md" sx={{ py: 8, textAlign: 'center' }}>
        <Typography variant="h4" gutterBottom>
          Article Not Found
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 4 }}>
          The article you are looking for does not exist or has been removed.
        </Typography>
        <Button component={RouterLink} to="/blog" variant="contained" startIcon={<ArrowBackIcon />}>
          Back to Articles
        </Button>
      </Container>
    );
  }

  if (isLoading) {
    return <ArticleSkeleton />;
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 4 }}>
          Failed to load article. Please try again later.
        </Alert>
        <Button component={RouterLink} to="/blog" variant="outlined" startIcon={<ArrowBackIcon />}>
          Back to Articles
        </Button>
      </Container>
    );
  }

  if (!post) {
    return null;
  }

  return (
    <>
      <BlogPostSEO post={post} />

      <Container maxWidth="md" sx={{ py: 4 }}>
        <Button
          component={RouterLink}
          to="/blog"
          startIcon={<ArrowBackIcon />}
          sx={{ mb: 3 }}
          color="inherit"
          onClick={() => trackEvent('cta_click', 'navigation', 'Back to Articles - Blog Post')}
        >
          Back to Articles
        </Button>

        <Typography
          component={RouterLink}
          to={`/blog?category=${encodeURIComponent(post.category)}`}
          variant="body2"
          sx={{
            display: 'block',
            color: 'primary.main',
            textDecoration: 'none',
            fontWeight: 500,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            fontSize: '0.75rem',
            mb: 1,
            '&:hover': {
              textDecoration: 'underline',
            },
          }}
        >
          {post.category}
        </Typography>

        <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 700, lineHeight: 1.2, mt: 1 }}>
          {post.title}
        </Typography>

        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            mb: 3,
          }}
        >
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Avatar src={post.author.avatar || undefined} alt={post.author.name} sx={{ width: 40, height: 40 }}>
                {post.author.name.charAt(0)}
              </Avatar>
              <Typography variant="body2">{post.author.name}</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CalendarTodayIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary">
                {formatDate(post.publishedDate)}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <AccessTimeIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary">
                {post.readingTime} min read
              </Typography>
            </Box>
          </Box>
          <ShareButton title={post.title} contentType="article" size="small" variant="text" />
        </Box>

        {post.featuredImage && (
          <Box
            component="img"
            src={post.featuredImage}
            alt={post.title}
            sx={{
              width: '100%',
              height: 'auto',
              borderRadius: 2,
              mb: 4,
            }}
          />
        )}

        <Divider sx={{ mb: 4 }} />

        <Box
          sx={{
            '& h2': { mt: 4, mb: 2, fontWeight: 600, fontSize: '1.75rem' },
            '& h3': { mt: 3, mb: 1.5, fontWeight: 600, fontSize: '1.35rem' },
            '& p': { mb: 2, lineHeight: 1.8, fontSize: '1.1rem' },
            '& ul, & ol': { pl: 3, mb: 2, '& li': { mb: 1, lineHeight: 1.7 } },
            '& a': { color: 'primary.main', textDecoration: 'underline', '&:hover': { textDecoration: 'none' } },
            '& blockquote': {
              borderLeft: 4,
              borderColor: 'primary.main',
              pl: 3,
              py: 1,
              my: 3,
              fontStyle: 'italic',
              bgcolor: 'action.hover',
              borderRadius: 1,
            },
            '& code': { bgcolor: 'action.hover', px: 0.5, py: 0.25, borderRadius: 0.5, fontFamily: 'monospace' },
            '& pre': {
              bgcolor: 'grey.900',
              color: 'grey.100',
              p: 2,
              borderRadius: 1,
              overflow: 'auto',
              '& code': { bgcolor: 'transparent', p: 0 },
            },
          }}
          dangerouslySetInnerHTML={{ __html: post.content }}
        />

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 4 }}>
          {post.tags.map((tag) => (
            <Chip key={tag} label={tag} size="small" variant="outlined" />
          ))}
        </Box>

        <RelatedBills billSlugs={post.relatedBills} />
        <AuthorBio author={post.author} />
        <ArticleNavigation currentSlug={post.slug} />
      </Container>
    </>
  );
};

export default BlogPostPage;
